<template>
  <div class="detail-page">
    <div class="back" @click="goBack">
      <a-button type="primary"><i class="iconfont icon-fanhuiicon mr5"></i> 返回</a-button>
    </div>

    <div class="detail-content">
      <div class="page-title">事故报告详情</div>

      <div class="info-section">
        <div class="section-title">事故基本信息</div>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">车辆编码</span>
            <span class="value">{{ accidentInfo.licenceNo || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">测试/运营主体</span>
            <span class="value">{{ accidentInfo.deptName || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">车辆识别码</span>
            <span class="value">{{ accidentInfo.vin || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">事故类型</span>
            <span class="value">{{ getAccidentTypeText(accidentInfo.accidentType) }}</span>
          </div>
          <div class="info-item">
            <span class="label">事故发生地点</span>
            <span class="value">{{ accidentInfo.happenedPlace || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">事故发生时间</span>
            <span class="value">{{ accidentInfo.happenedTime || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">事故状态</span>
            <span class="value">{{ getStatusText(accidentInfo.status) }}</span>
          </div>
          <div class="info-item full-width">
            <span class="label">事故情况说明</span>
            <span class="value">{{ accidentInfo.accidentContext || '-' }}</span>
          </div>
        </div>
      </div>

      <div class="info-section">
        <div class="section-title">事故报告信息</div>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">经度</span>
            <span class="value">{{ accidentInfo.lon || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">纬度</span>
            <span class="value">{{ accidentInfo.lat || '-' }}</span>
          </div>
          <div class="info-item">
            <span class="label">天气</span>
            <span class="value">{{ getWeatherText(accidentInfo.weather) }}</span>
          </div>
          <div class="info-item">
            <span class="label">路面状况</span>
            <span class="value">{{ getRoadConditionText(accidentInfo.roadCondition) }}</span>
          </div>
          <div class="info-item">
            <span class="label">事故类型</span>
            <span class="value">{{ getAccidentTypeText(accidentInfo.accidentType) }}</span>
          </div>
          <div class="info-item">
            <span class="label">严重等级</span>
            <span class="value">{{ getSeverityLevelText(accidentInfo.severityLevel) }}</span>
          </div>
          <div class="info-item full-width">
            <span class="label">事故说明</span>
            <span class="value">{{ accidentInfo.description || '未填写' }}</span>
          </div>
        </div>
      </div>

      <div class="info-section">
        <div class="section-title">事故图片</div>
        <images-uploader
          :imageUrls="accidentInfo.images || []"
          :onlyPreview="true"
        />
      </div>

      <div class="info-section">
        <div class="section-title">事故视频</div>
        <videos-uploader
          :videoUrls="accidentInfo.videos || []"
          :onlyPreview="true"
        />
      </div>

      <div class="info-section">
        <div class="section-title">事故处理报告</div>
        <div v-if="accidentInfo.handlingReports && accidentInfo.handlingReports.length > 0" class="file-list">
          <div v-for="(report, index) in accidentInfo.handlingReports" :key="index" class="file-item">
            <file-text-outlined class="file-icon" />
            <a class="file-name" :href="report.url || report" target="_blank">{{ report.name || getFileName(report.url || report) }}</a>
          </div>
        </div>
        <div v-else class="empty-state">
          未上传事故处理报告
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { FileTextOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { detailIncident } from '@/api/report/incident';
import imagesUploader from '@/components/imagesUploader/index.vue';
import videosUploader from '@/components/VideosUploader/index.vue';

const router = useRouter();
const route = useRoute();
const accidentInfo = ref({});

const getAccidentTypeText = (type) => {
  const typeMap = { 1: '碰撞', 2: '刮擦', 3: '追尾', 4: '翻车' };
  return typeMap[type] || '-';
};

const getWeatherText = (weather) => {
  const map = { 1: '晴', 2: '雨', 3: '雾', 4: '雪' };
  return map[weather] || '-';
};

const getRoadConditionText = (condition) => {
  const map = { 1: '干燥', 2: '湿滑', 3: '结冰' };
  return map[condition] || '-';
};

const getSeverityLevelText = (level) => {
  const map = { 1: '一般', 2: '较大', 3: '重大', 4: '特大' };
  return map[level] || '-';
};

const getStatusText = (status) => {
  const statusMap = { 0: '待上传', 1: '已上传', 2: '已结案', 3: '已归档' };
  return statusMap[status] || '-';
};

const getFileName = (url) => {
  if (!url) return '-';
  const decoded = decodeURIComponent(url);
  return decoded.substring(decoded.lastIndexOf('/') + 1);
};

const loadAccidentDetail = async () => {
  const incidentId = route.query.incidentId;
  if (!incidentId) {
    message.error('缺少事故ID');
    goBack();
    return;
  }
  try {
    const res = await detailIncident(incidentId);
    if (res.code === 200) {
      accidentInfo.value = res.data || {};
    }
  } catch (error) {
    console.error('获取事故详情失败:', error);
    message.error('获取事故详情失败');
  }
};

const goBack = () => {
  router.push('/testManage/incident');
};

onMounted(() => {
  loadAccidentDetail();
});
</script>

<style lang="less" scoped>
.detail-page {
  padding: 20px;
  background-color: #ffffff;
  min-height: calc(100vh - 70px - 57px);
}

.back {
  position: fixed;
  top: 93px;
  right: 50px;
  z-index: 20;
}

.detail-content {
  background: #F7F8FA;
  border-radius: 8px;
  padding: 30px;
  max-width: 1400px;
  margin: 0 auto;
}

.page-title {
  font-size: 20px;
  font-weight: bold;
  color: #0A212E;
  margin-bottom: 24px;
  text-align: center;
}

.info-section {
  background: #ffffff;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
}

.section-title {
  font-size: 16px;
  font-weight: bold;
  color: #0A212E;
  margin-bottom: 16px;
  padding-bottom: 10px;
  border-bottom: 1px solid #E1E7EF;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;

  &.full-width {
    grid-column: span 3;
  }

  .label {
    font-size: 14px;
    color: #68767E;
    margin-bottom: 8px;
  }

  .value {
    font-size: 14px;
    color: #0A212E;
    font-weight: 500;
  }
}

.file-list {
  margin-top: 12px;
}

.file-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background: #F7F8FA;
  border-radius: 4px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.file-icon {
  font-size: 18px;
  color: #1890ff;
  margin-right: 10px;
}

.file-name {
  flex: 1;
  font-size: 14px;
  color: #1890ff;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.empty-state {
  font-size: 14px;
  color: #999999;
  padding: 20px;
  text-align: center;
  background: #F7F8FA;
  border-radius: 4px;
  margin-top: 12px;
}

/deep/ .ant-table-wrapper .ant-table-thead>tr>th,
.ant-table-wrapper .ant-table-thead>tr>td {
  font-size: 14px;
  font-family: PingFang SC-Regular, PingFang SC;
  font-weight: 400;
  color: #0A212E;
  background-color: #EFEFF5;
}

/deep/.ant-table-wrapper .ant-table:not(.ant-table-bordered) .ant-table-tbody>tr {
  color: rgba(104, 118, 126, 1);
}

/deep/ .ant-table-wrapper .ant-table-thead>tr>th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before,
.ant-table-wrapper .ant-table-thead>tr>td:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan])::before {
  width: 0px;
}
</style>
